import { useState, useEffect, useCallback, useRef } from 'react';
import { syncService, ServiceAccountCredentials } from '../services';
import { useDatabase } from '../db/DatabaseProvider';
import { useSettingsStore } from './useSettings';
import type { LancarPOSDatabase } from '@lancar-pos/db-schema';
import type { SyncResult } from '../workers/syncWorkerBridge';
import type { WorkerToMainMessage, MainToWorkerMessage } from '../workers/syncWorkerBridge';

interface UseSyncReturn {
  isSyncing: boolean;
  isOnline: boolean;
  lastSyncAt: string | null;
  error: string | null;
  queueStats: { pending: number; failed: number; total: number };
  isConfigured: boolean;
  syncAll: () => Promise<SyncResult[]>;
  pullFromSheets: () => Promise<SyncResult[]>;
  pushToSheets: () => Promise<SyncResult[]>;
  retryFailed: () => void;
}

async function queryCollectionData(
  db: LancarPOSDatabase,
  collection: string,
  lastSyncAt: string | null
): Promise<Record<string, unknown>[]> {
  const lastSync = lastSyncAt ? new Date(lastSyncAt) : null;
  
  let query: any;
  
  switch (collection) {
    case 'products':
      query = lastSync
        ? db.products.find().where('updatedAt').gt(lastSync.toISOString())
        : db.products.find();
      break;
    case 'categories':
      query = lastSync
        ? db.categories.find().where('updatedAt').gt(lastSync.toISOString())
        : db.categories.find();
      break;
    case 'orders':
      query = lastSync
        ? db.orders.find().where('updatedAt').gt(lastSync.toISOString())
        : db.orders.find();
      break;
    case 'customers':
      query = lastSync
        ? db.customers.find().where('updatedAt').gt(lastSync.toISOString())
        : db.customers.find();
      break;
    default:
      return [];
  }
  
  const docs = await query.exec();
  return docs.map((doc: any) => doc.toJSON());
}

async function upsertFromPull(
  db: LancarPOSDatabase,
  collection: string,
  documents: { id: string; data: Record<string, unknown>; updatedAt: string }[]
): Promise<void> {
  for (const doc of documents) {
    const existing = await (db as any)[collection]
      .findOne(doc.id)
      .exec();

    if (existing) {
      await (db as any)[collection].upsert({
        ...doc.data,
        id: doc.id,
      });
    } else {
      await (db as any)[collection].insert(doc.data);
    }
  }
}

export function useSync(): UseSyncReturn {
  const db = useDatabase();
  const { getSetting } = useSettingsStore();
  const [isSyncing, setIsSyncing] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [lastSyncAt, setLastSyncAt] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [queueStats, setQueueStats] = useState({ pending: 0, failed: 0, total: 0 });
  const workerRef = useRef<Worker | null>(null);
  const dbRef = useRef<LancarPOSDatabase>(db);
  const syncingRef = useRef(false);

  dbRef.current = db;

  const credentials = getSetting('sheets_credentials') as string | null;
  const spreadsheetId = getSetting('sheets_spreadsheet_id') as string | null;
  const isConfigured = Boolean(credentials && spreadsheetId);

  const autoSyncEnabled = getSetting<boolean>('sync_auto_enabled') ?? true;
  const syncIntervalMinutes = getSetting<number>('sync_interval_minutes') ?? 15;
  const syncOnStartup = getSetting<boolean>('sync_on_startup') ?? true;

  const [initialLastSyncAt, setInitialLastSyncAt] = useState<string | null>(null);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    const loadInitialState = async () => {
      await syncService.setDatabase(db);
      const status = syncService.getStatus();
      setInitialLastSyncAt(status.lastSyncAt);
      setLastSyncAt(status.lastSyncAt);
      setQueueStats(status.queueStats);
    };
    loadInitialState();
  }, [db]);

  useEffect(() => {
    if (!isConfigured) return;

    const worker = new Worker(
      new URL('../workers/syncWorker.ts', import.meta.url),
      { type: 'module' }
    );
    workerRef.current = worker;

    const handleMessage = async (event: MessageEvent<WorkerToMainMessage>) => {
      const message = event.data;

      switch (message.type) {
        case 'READY':
          if (credentials && spreadsheetId) {
            try {
              const parsedCredentials = JSON.parse(credentials) as ServiceAccountCredentials;
              worker.postMessage({
                type: 'INIT',
                credentials: parsedCredentials,
                spreadsheetId,
                lastSyncAt: initialLastSyncAt,
              } as MainToWorkerMessage);
              
              if (autoSyncEnabled) {
                worker.postMessage({
                  type: 'SET_INTERVAL',
                  minutes: syncIntervalMinutes,
                  enabled: true,
                } as MainToWorkerMessage);
              }

              if (syncOnStartup) {
                worker.postMessage({ type: 'SYNC_ALL' } as MainToWorkerMessage);
              }
            } catch {
              console.error('Failed to parse credentials');
            }
          }
          break;

        case 'STATUS':
          setIsSyncing(message.status.isSyncing);
          setLastSyncAt(message.status.lastSyncAt);
          setError(message.status.error);
          setQueueStats(message.status.queueStats);
          syncingRef.current = message.status.isSyncing;
          break;

        case 'RESULT':
          break;

        case 'ERROR':
          setError(message.error);
          break;

        case 'REQUEST_DATA':
          if (message.collections && message.collections.length > 0) {
            const allData: Record<string, unknown>[] = [];
            for (const collection of message.collections) {
              const data = await queryCollectionData(dbRef.current, collection, lastSyncAt);
              allData.push(...data);
            }
            worker.postMessage({
              type: 'DATA_RESPONSE',
              collection: message.collections[0],
              data: allData,
            } as MainToWorkerMessage);
          }
          break;

        case 'PULL_DATA':
          if (message.collection && message.documents) {
            await upsertFromPull(dbRef.current, message.collection, message.documents);
          }
          break;
      }
    };

    worker.onmessage = handleMessage;

    return () => {
      worker.terminate();
      workerRef.current = null;
    };
  }, [isConfigured, credentials, spreadsheetId, autoSyncEnabled, syncIntervalMinutes, syncOnStartup, initialLastSyncAt]);

  useEffect(() => {
    if (!workerRef.current || !isConfigured) return;

    workerRef.current.postMessage({
      type: 'SET_INTERVAL',
      minutes: syncIntervalMinutes,
      enabled: autoSyncEnabled,
    } as MainToWorkerMessage);
  }, [autoSyncEnabled, syncIntervalMinutes, isConfigured]);

  useEffect(() => {
    if (!workerRef.current || !db) return;

    const persistLastSync = async () => {
      if (lastSyncAt) {
        await syncService.persistLastSyncAt(lastSyncAt);
      }
    };
    persistLastSync();
  }, [lastSyncAt, db]);

  const syncAll = useCallback(async () => {
    if (syncingRef.current) {
      throw new Error('Sync already in progress');
    }

    if (workerRef.current && isConfigured) {
      syncingRef.current = true;
      setIsSyncing(true);
      setError(null);
      workerRef.current.postMessage({ type: 'SYNC_ALL' } as MainToWorkerMessage);
      return [];
    }

    syncingRef.current = true;
    setIsSyncing(true);
    setError(null);

    try {
      const result = await syncService.syncAll();
      return result;
    } finally {
      syncingRef.current = false;
      setIsSyncing(false);
    }
  }, [isConfigured]);

  const pullFromSheets = useCallback(async () => {
    if (syncingRef.current) {
      throw new Error('Sync already in progress');
    }

    if (workerRef.current && isConfigured) {
      syncingRef.current = true;
      setIsSyncing(true);
      setError(null);
      workerRef.current.postMessage({ type: 'PULL' } as MainToWorkerMessage);
      return [];
    }

    syncingRef.current = true;
    setIsSyncing(true);
    setError(null);

    try {
      const result = await syncService.pullFromSheets();
      return result;
    } finally {
      syncingRef.current = false;
      setIsSyncing(false);
    }
  }, [isConfigured]);

  const pushToSheets = useCallback(async () => {
    if (syncingRef.current) {
      throw new Error('Sync already in progress');
    }

    if (workerRef.current && isConfigured) {
      syncingRef.current = true;
      setIsSyncing(true);
      setError(null);
      workerRef.current.postMessage({ type: 'PUSH' } as MainToWorkerMessage);
      return [];
    }

    syncingRef.current = true;
    setIsSyncing(true);
    setError(null);

    try {
      const result = await syncService.pushToSheets();
      return result;
    } finally {
      syncingRef.current = false;
      setIsSyncing(false);
    }
  }, [isConfigured]);

  const retryFailed = useCallback(() => {
    syncService.retryFailed();
    setQueueStats(syncService.getStatus().queueStats);
  }, []);

  return {
    isSyncing,
    isOnline,
    lastSyncAt,
    error,
    queueStats,
    isConfigured,
    syncAll,
    pullFromSheets,
    pushToSheets,
    retryFailed,
  };
}
